import React from 'react'
import { BottomSheet } from '@/commons/BottomSheet'
import { AirconGeneralServiceProcess } from './AirconGeneralServiceProcess'
import { AirconChemicalWashProcess } from './AirconChemicalWashProcess'
import { AirconChemicalOverhaulProcess } from './AirconChemicalOverhaulProcess'
import { FaWind, FaGear, FaDroplet, FaWrench, FaSnowflake } from 'react-icons/fa6'
import { MdOutlineCleaningServices } from 'react-icons/md'
import { TbAirConditioning } from 'react-icons/tb'
import { localiseTerms, t } from '../../../../utils/markets'

// Market term for each sheet title (UAE / Malaysia), e.g. general -> "AC Cleaning".
const TITLE_TERM = { general: 'acGeneral', generalCeiling: 'acGeneral', chemicalWash: 'acChemicalWash', chemicalOverhaul: 'acChemicalOverhaul', gasRefill: 'acGas', gasRefillHalf: 'acGas' }
import { formatMoney } from '../../../../utils/format-money'

// Service type configurations
const serviceTypeConfig = {
    general: {
        title: "General Service",
        subtitle: "Regular maintenance",
        icon: <FaWind className="text-xl" />,
        gradient: "from-blue-500 to-cyan-500",
        hasProcess: true,
        ProcessComponent: AirconGeneralServiceProcess
    },
    generalCeiling: {
        title: "General Service",
        subtitle: "Ceiling-mounted aircon maintenance",
        icon: <FaWind className="text-xl" />,
        gradient: "from-blue-500 to-cyan-500",
        hasProcess: true,
        ProcessComponent: AirconGeneralServiceProcess,
        defaultUnitType: "Ceiling"
    },
    chemicalOverhaul: {
        title: "Chemical Overhaul",
        subtitle: "Deep cleaning with full dismantling",
        icon: <FaGear className="text-xl" />,
        gradient: "from-purple-500 to-indigo-500",
        hasProcess: true,
        ProcessComponent: AirconChemicalOverhaulProcess
    },
    chemicalHydroWash: {
        title: "Chemical Hydro Wash",
        subtitle: "Partial dismantle and water-jet rinse",
        icon: <FaDroplet className="text-xl" />,
        gradient: "from-indigo-500 to-blue-500",
        hasProcess: false,
        description: "More thorough than a chemical wash, without the full strip-down of an overhaul. The technician removes the front panel, filters, blower wheel and drain tray, treats the coil and blower with a cleaning chemical and rinses them with a high-pressure water jet, then reassembles and tests the unit. A good choice for a musty smell, weak airflow or a dirty blower when the unit does not need to come off the wall."
    },
    chemicalWash: {
        title: "Chemical Wash",
        subtitle: "Thorough chemical cleaning service",
        icon: <MdOutlineCleaningServices className="text-xl" />,
        gradient: "from-emerald-500 to-teal-500",
        hasProcess: true,
        ProcessComponent: AirconChemicalWashProcess
    },
    repair: {
        title: "Visit & Troubleshoot",
        subtitle: "Diagnosis and repair service",
        icon: <FaWrench className="text-xl" />,
        gradient: "from-red-500 to-orange-500",
        hasProcess: false,
        description: "Our technician will visit your place to troubleshoot and let you know what needs to be serviced. They will only proceed with the needed service if you approve the cost, otherwise you will just pay the visit cost as reflected while booking."
    },
    gasRefill: {
        title: "Gas Top-up",
        subtitle: "Refrigerant gas refill service",
        icon: <FaDroplet className="text-xl" />,
        gradient: "from-amber-500 to-yellow-500",
        hasProcess: false,
        defaultUnitType: "Outdoor",
        description: "Book a gas refill service for your aircon compressor. Our technician will refill the gas and check for any leaks. The price is based on the number of units you need to refill. If you are not sure how many units you need, please select 1 unit and our technician will advise you on the spot."
    },
    gasRefillHalf: {
        title: "Gas Top-up",
        subtitle: "Partial refrigerant gas refill",
        icon: <FaDroplet className="text-xl" />,
        gradient: "from-amber-500 to-yellow-500",
        hasProcess: false,
        defaultUnitType: "Outdoor",
        description: "Book a gas refill service for your aircon compressor. Our technician will refill the gas and check for any leaks. The price is based on the number of units you need to refill. If you are not sure how many units you need, please select 1 unit and our technician will advise you on the spot."
    },
    compressorCleaning: {
        title: "Compressor General Service",
        subtitle: "Outdoor unit maintenance",
        icon: <TbAirConditioning className="text-xl" />,
        gradient: "from-sky-500 to-blue-500",
        hasProcess: false,
        defaultUnitType: "Outdoor",
        description: "Our technician will clean the compressor with cloth, brush & vacuuming without any dismantling. This service is needed for the compressor every 3-6 months to ensure that it is running efficiently. If the compressor is not cleaned for a long time, it may lead to overheating and malfunctioning."
    },
    compressorChemicalCleaning: {
        title: "Compressor Chemical Wash",
        subtitle: "Deep chemical cleaning for outdoor unit",
        icon: <FaSnowflake className="text-xl" />,
        gradient: "from-violet-500 to-purple-500",
        hasProcess: false,
        defaultUnitType: "Outdoor",
        description: "Our technician will clean the compressor with chemical solution. This is a more thorough cleaning than the general cleaning. This service is needed for the compressor every 6-12 months to ensure that it is running efficiently. If the compressor is not cleaned for a long time, it may lead to overheating and malfunctioning."
    }
}

export const AirconScope = (props) => {
    // market (UAE / Malaysia only): local terms and currency. SG passes none.
    const { setIsShowJobScope, serviceType, unit, unitType, cost, market } = props
    const isOpen = true

    const handleClose = () => {
        setIsShowJobScope(false)
    }

    const baseConfig = serviceTypeConfig[serviceType] || serviceTypeConfig.general
    const config = market ? {
        ...baseConfig,
        title: TITLE_TERM[serviceType] ? t(market, TITLE_TERM[serviceType]) : localiseTerms(baseConfig.title, market),
        subtitle: localiseTerms(baseConfig.subtitle, market),
        description: localiseTerms(baseConfig.description, market),
    } : baseConfig
    const ProcessComponent = config.ProcessComponent

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="2xl"
        >
            {/* Header */}
            <BottomSheet.Header>
                <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-r ${config.gradient} text-white`}>
                        {config.icon}
                    </div>
                    <div>
                        <BottomSheet.Title>{config.title}</BottomSheet.Title>
                        <BottomSheet.Subtitle>{config.subtitle}</BottomSheet.Subtitle>
                    </div>
                </div>
            </BottomSheet.Header>

            {/* Content */}
            <BottomSheet.Content className="space-y-6 text-gray-700 text-sm leading-6">
                {/* Price/Unit Info Card */}
                {cost ? (
                    <PriceCard unit={unit} unitType={unitType} cost={cost} config={config} market={market} />
                ) : (
                    <UnitTypeCard unitType={config.defaultUnitType || unitType || "Wall"} config={config} market={market} />
                )}

                {/* Service Process or Description */}
                {config.hasProcess && ProcessComponent ? (
                    <div className="space-y-4">
                        <ProcessComponent market={market} />
                    </div>
                ) : config.description ? (
                    <DescriptionCard description={config.description} config={config} />
                ) : null}
            </BottomSheet.Content>

            {/* Footer */}
            <BottomSheet.Footer className="flex gap-3">
                <BottomSheet.CloseButton variant="primary">
                    Got it!
                </BottomSheet.CloseButton>
            </BottomSheet.Footer>
        </BottomSheet>
    )
}

// Price Card Component
const PriceCard = ({ unit, unitType, cost, config, market }) => (
    <div className={`bg-linear-to-r ${config.gradient} rounded-2xl p-4 text-white`}>
        <div className="flex items-center justify-between">
            <div>
                <div className="text-white/80 text-xs uppercase tracking-wider mb-1">Selected Package</div>
                <div className="text-2xl font-bold">{unit} Units</div>
                <div className="text-white/80 text-sm mt-1">{unitType} Mount Type</div>
            </div>
            <div className="text-right">
                <div className="text-white/80 text-xs uppercase tracking-wider mb-1">Total Price</div>
                <div className="text-3xl font-bold">{market ? formatMoney(cost, market.countryCd) : `$${cost}`}</div>
            </div>
        </div>
    </div>
)

// Unit Type Card Component
const UnitTypeCard = ({ unitType, config, market }) => (
    <div className={`bg-linear-to-r from-gray-100 to-gray-50 rounded-2xl p-4 border border-gray-200`}>
        <div className="flex items-center justify-center gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-r ${config.gradient} text-white`}>
                {config.icon}
            </div>
            <div className="text-center">
                <div className="text-gray-500 text-xs uppercase tracking-wider">{localiseTerms('Aircon Unit Type', market)}</div>
                <div className="text-lg font-bold text-gray-800">{unitType}</div>
            </div>
        </div>
    </div>
)

// Description Card Component
const DescriptionCard = ({ description, config }) => (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <p className="text-gray-700 leading-relaxed">
            {description}
        </p>
    </div>
)
